/**
 * HLSプレーヤークラス（hls.js使用）
 * SRSとOMEのHLS/LL-HLS再生に対応
 */

import Hls from 'hls.js';
import type { HlsConfig } from 'hls.js';
import type { PlayerConfig, PlaybackStats } from './types';

export class HLSPlayer {
  private hls: Hls | null = null;
  private videoElement: HTMLVideoElement;
  private config: PlayerConfig;
  private statsInterval: ReturnType<typeof setInterval> | null = null;
  private lastDecodedFrames = 0;
  private lastTimestamp = Date.now();
  private isLowLatency: boolean;
  private mediaErrorRecoveryAttempts = 0;
  private static readonly MAX_MEDIA_ERROR_RECOVERY_ATTEMPTS = 3;

  constructor(videoElement: HTMLVideoElement, config: PlayerConfig, lowLatency = false) {
    this.videoElement = videoElement;
    this.config = config;
    this.isLowLatency = lowLatency;
  }

  /**
   * HLS再生を開始
   */
  async start(): Promise<void> {
    if (this.hls) {
      throw new Error('Player is already initialized');
    }

    if (!Hls.isSupported()) {
      throw new Error('HLS is not supported in this browser');
    }

    const url = this.buildHLSUrl();
    console.log('[HLS] Starting playback:', url);

    if (this.config.onStatusChange) {
      this.config.onStatusChange('starting');
    }

    // hls.js初期化
    // VPS環境でのバッファエラー対策: バッファサイズを増加
    const hlsConfig = {
      // 低遅延設定（VPS対応で緩和）
      // 注意: liveSyncDuration/liveMaxLatencyDuration と liveSyncDurationCount は混在不可
      ...(this.isLowLatency && {
        lowLatencyMode: true,
        backBufferLength: 90,      // 90秒（デフォルトは無限）
        maxBufferLength: 30,       // 30秒に増加（10秒→30秒）VPS遅延対策
        maxMaxBufferLength: 60,    // 60秒に増加（20秒→60秒）
        liveSyncDuration: 6,       // 6秒に緩和（3秒→6秒）バッファ安定化
        liveMaxLatencyDuration: 15, // 15秒に緩和（10秒→15秒）
        // liveSyncDurationCount は liveSyncDuration と併用不可のため削除
      }),
      // 通常のHLS設定
      ...(!this.isLowLatency && {
        maxBufferLength: 30,
        maxMaxBufferLength: 600,
      }),
      // 共通設定: エラー耐性向上
      fragLoadingMaxRetry: 6,      // フラグメント読み込みリトライ回数
      fragLoadingRetryDelay: 1000, // リトライ間隔(ms)
      manifestLoadingMaxRetry: 4,  // マニフェスト読み込みリトライ
      levelLoadingMaxRetry: 4,     // レベル読み込みリトライ
      // バッファ隙間の許容設定（Windows配信対策）
      maxBufferHole: 2.0,          // 許容する最大バッファ隙間（1秒→2秒）
      // hls.js supports this option at runtime, but this installed type
      // definition does not expose it.
      maxSeekHole: 5,              // シーク時に飛ばす最大隙間（3秒→5秒）
      nudgeMaxRetry: 10,           // スタック時のリトライ回数（5→10）
      nudgeOffset: 0.2,            // スタック時に進める秒数（デフォルト0.1→0.2秒）
    } as Partial<HlsConfig> & { maxSeekHole: number };

    this.hls = new Hls(hlsConfig);

    // イベントリスナー設定
    this.setupEventListeners();

    // メディアをアタッチ
    this.hls.loadSource(url);
    this.hls.attachMedia(this.videoElement);

    // バッファリング状態の監視を開始
    this.setupBufferingListeners();

    // 統計情報の監視を開始
    this.startStatsMonitoring();
  }

  /**
   * バッファリング状態を監視するリスナーを設定
   */
  private setupBufferingListeners(): void {
    // バッファリング開始（動画が止まった）
    this.videoElement.addEventListener('waiting', () => {
      console.log('[HLS] Buffering started');
      if (this.config.onBufferingChange) {
        this.config.onBufferingChange(true);
      }
    });

    // 再生再開（バッファリング終了）
    this.videoElement.addEventListener('playing', () => {
      console.log('[HLS] Buffering ended, playback resumed');
      if (this.config.onBufferingChange) {
        this.config.onBufferingChange(false);
      }
    });
  }

  /**
   * HLS再生を停止
   */
  async stop(): Promise<void> {
    this.cleanup();

    if (this.config.onStatusChange) {
      this.config.onStatusChange('stopped');
    }
  }

  /**
   * HLS URLを構築
   */
  private buildHLSUrl(): string {
    const { serverType, streamKey } = this.config;

    // Read from environment variables for Railway deployment compatibility
    const SRS_HOST = import.meta.env.VITE_SRS_HOST || 'localhost';
    const SRS_HTTP_PORT = import.meta.env.VITE_SRS_HTTP_PORT || '8080';
    const OME_HOST = import.meta.env.VITE_OME_HOST || 'localhost';
    const OME_HLS_PORT = import.meta.env.VITE_OME_HLS_PORT || '8081';

    // Determine protocol based on current page (to avoid mixed content issues)
    const isSecure = window.location.protocol === 'https:';
    const HTTP_PROTOCOL = isSecure ? 'https' : 'http';

    // キャッシュバスターを追加（ストリーム再開時に古いキャッシュを使わないように）
    const cacheBuster = `?t=${Date.now()}`;

    if (serverType === 'srs') {
      // Phase0-3: SRS HLS URL with cache busting (urgent fix)
      return `${HTTP_PROTOCOL}://${SRS_HOST}:${SRS_HTTP_PORT}/live/${streamKey}.m3u8${cacheBuster}`;
    } else {
      // OME LL-HLS URL (low latency mode uses llhls.m3u8, standard uses playlist.m3u8)
      const hlsFile = this.isLowLatency ? 'llhls.m3u8' : 'playlist.m3u8';

      // Use transcoded stream (without _webrtc suffix) for HLS
      // The _webrtc passthrough stream uses OPUS which is not supported by HLS
      // The transcoded stream converts OPUS to AAC for HLS compatibility
      const omeStreamKey = streamKey;

      // In production (HTTPS), use nginx proxy path (no port)
      // In development (HTTP), use direct OME port
      if (isSecure) {
        // Use nginx proxy: https://domain/app/...
        return `${HTTP_PROTOCOL}://${OME_HOST}/app/${omeStreamKey}/${hlsFile}${cacheBuster}`;
      } else {
        // Direct connection: http://host:8081/app/...
        return `${HTTP_PROTOCOL}://${OME_HOST}:${OME_HLS_PORT}/app/${omeStreamKey}/${hlsFile}${cacheBuster}`;
      }
    }
  }

  /**
   * hls.jsイベントリスナーを設定
   */
  private setupEventListeners(): void {
    if (!this.hls) return;

    // マニフェストがロードされた
    this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
      console.log('[HLS] Manifest parsed, starting playback');
      this.videoElement.play().catch(err => {
        console.error('[HLS] Autoplay failed:', err);
        if (this.config.onError) {
          this.config.onError(new Error('Autoplay failed'));
        }
      });

      // 再生成功時にエラーカウンターをリセット
      this.mediaErrorRecoveryAttempts = 0;

      if (this.config.onStatusChange) {
        this.config.onStatusChange('playing');
      }
    });

    // フラグメントがロードされた（デバッグ用、コメントアウト）
    // this.hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
    //   console.log('[HLS] Fragment loaded:', data.frag.sn, `(${data.frag.duration.toFixed(2)}s)`);
    // });

    // エラー処理
    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      // Phase0-3: Enhanced error logging (console only, NO UI changes)
      console.error('[HLS] Error:', data.type, data.details, data.fatal);
      console.log('[Phase0-3 Error Log] Type:', data.type, '| Details:', data.details, '| Fatal:', data.fatal, '| Timestamp:', new Date().toISOString());

      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.error('[HLS] Fatal network error');
            console.log('[Phase0-3 Error Log] Network error occurred - Fatal network error, connection may be lost');
            if (this.config.onError) {
              this.config.onError(new Error('Network error'));
            }
            if (this.config.onStatusChange) {
              this.config.onStatusChange('error');
            }
            break;

          case Hls.ErrorTypes.MEDIA_ERROR:
            this.mediaErrorRecoveryAttempts++;
            console.error(`[HLS] Fatal media error, attempting recovery (attempt ${this.mediaErrorRecoveryAttempts}/${HLSPlayer.MAX_MEDIA_ERROR_RECOVERY_ATTEMPTS})`);
            console.log('[Phase0-3 Error Log] Media error occurred - Attempting automatic recovery');

            if (this.mediaErrorRecoveryAttempts <= HLSPlayer.MAX_MEDIA_ERROR_RECOVERY_ATTEMPTS) {
              if (this.mediaErrorRecoveryAttempts === 1) {
                // 1回目: 通常のリカバリー
                console.log('[HLS] Recovery strategy 1: recoverMediaError()');
                this.hls?.recoverMediaError();
              } else if (this.mediaErrorRecoveryAttempts === 2) {
                // 2回目: オーディオコーデックスワップ後にリカバリー
                console.log('[HLS] Recovery strategy 2: swapAudioCodec() + recoverMediaError()');
                this.hls?.swapAudioCodec();
                this.hls?.recoverMediaError();
              } else {
                // 3回目: プレイヤー再初期化
                console.log('[HLS] Recovery strategy 3: Full player reinitialize');
                this.hls?.destroy();
                this.hls = null;
                // 少し待ってから再初期化
                setTimeout(() => {
                  this.start().catch(err => {
                    console.error('[HLS] Failed to reinitialize player:', err);
                    if (this.config.onStatusChange) {
                      this.config.onStatusChange('error');
                    }
                  });
                }, 1000);
              }
            } else {
              // リカバリー上限超過
              console.error('[HLS] Max recovery attempts exceeded');
              if (this.config.onError) {
                this.config.onError(new Error('Media error: max recovery attempts exceeded'));
              }
              if (this.config.onStatusChange) {
                this.config.onStatusChange('error');
              }
            }
            break;

          default:
            console.error('[HLS] Fatal error, cannot recover');
            console.log('[Phase0-3 Error Log] Unrecoverable error - Cleanup initiated');
            this.cleanup();
            if (this.config.onError) {
              this.config.onError(new Error('Unrecoverable error'));
            }
            if (this.config.onStatusChange) {
              this.config.onStatusChange('error');
            }
            break;
        }
      }
    });
  }

  /**
   * 統計情報の監視を開始
   */
  private startStatsMonitoring(): void {
    this.statsInterval = setInterval(() => {
      if (!this.videoElement || !this.hls) return;

      try {
        // ビデオ品質情報
        const videoWidth = this.videoElement.videoWidth;
        const videoHeight = this.videoElement.videoHeight;

        // FPS計算（概算）
        const currentFrames = (this.videoElement as any).webkitDecodedFrameCount ||
                             (this.videoElement as any).mozDecodedFrames ||
                             0;
        const now = Date.now();
        const deltaTime = (now - this.lastTimestamp) / 1000;
        const deltaFrames = currentFrames - this.lastDecodedFrames;
        const fps = deltaTime > 0 && this.lastDecodedFrames > 0 ? Math.round(deltaFrames / deltaTime) : 0;

        // ビットレート計算（hls.jsの統計から）
        const levels = this.hls.levels;
        const currentLevel = this.hls.currentLevel;
        const bitrate = currentLevel >= 0 && levels[currentLevel] ?
          Math.round(levels[currentLevel].bitrate / 1000) : 0;

        // バッファ長
        const buffered = this.videoElement.buffered;
        const bufferLength = buffered.length > 0 ?
          buffered.end(buffered.length - 1) - this.videoElement.currentTime : 0;

        // 統計情報を通知
        if (this.config.onStatsUpdate) {
          const stats: PlaybackStats = {
            bitrate,
            fps,
            resolution: videoWidth && videoHeight ? `${videoWidth}×${videoHeight}` : undefined,
            bufferLength,
          };

          this.config.onStatsUpdate(stats);
        }

        // 次回のために保存
        this.lastDecodedFrames = currentFrames;
        this.lastTimestamp = now;

      } catch (err) {
        console.warn('[HLS] Stats monitoring error:', err);
      }
    }, 1000);
  }

  /**
   * クリーンアップ
   */
  private cleanup(): void {
    // 統計情報監視の停止
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }

    // hls.jsの破棄
    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }

    // ビデオ要素のリセット
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.src = '';
      this.videoElement.load();
    }

    console.log('[HLS] Cleanup complete');
  }

  /**
   * プレーヤーを破棄
   */
  destroy(): void {
    this.cleanup();
  }
}
